import QrCode2 from "@mui/icons-material/QrCode2";
import Close from "@mui/icons-material/Close";
import Print from "@mui/icons-material/Print";
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import EncampLogo from "../../assets/EncampLogo.svg";
import "./QRCodeDisplay.css";

interface QRCodeDisplayProps {
  open: boolean;
  onClose: () => void;
  targetUrl?: string;
}

// CloudFront URL for QR code generator service
// @ts-ignore - VITE env vars
const QR_CODE_SERVICE_URL =
  import.meta.env.VITE_QR_CODE_SERVICE_URL ||
  "https://d1z9fywttblej4.cloudfront.net";

export function QRCodeDisplay({
  open,
  onClose,
  targetUrl,
}: QRCodeDisplayProps) {
  const theme = useTheme();
  const [description, setDescription] = useState("");
  const [qrCodeUrl, setQrCodeUrl] = useState("");

  useEffect(() => {
    if (open) {
      // Always use consistent default text, never use window title
      const defaultDescription =
        "Use this QR code to access the Encamp application";
      setDescription(defaultDescription);

      // Generate QR code URL for target URL or current page
      const urlToEncode = targetUrl || window.location.href;
      const params = new URLSearchParams({
        text: urlToEncode,
        width: "400", // 400px for display, scales well for print
        margin: "2",
        errorCorrectionLevel: "H", // High error correction for better scanning
        color: theme.palette.mode === "dark" ? "ffffff" : "000000",
        backgroundColor: theme.palette.mode === "dark" ? "1a1a1a" : "ffffff",
      });

      setQrCodeUrl(`${QR_CODE_SERVICE_URL}?${params.toString()}`);
    }
  }, [open, theme.palette.mode, targetUrl]);

  const handlePrint = () => {
    window.print();
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDescription(e.target.value);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullScreen
      className="qr-code-dialog"
    >
      <DialogContent className="qr-code-content">
        {/* Header with close and print buttons */}
        <Box className="qr-code-header no-print">
          <IconButton
            onClick={onClose}
            sx={{
              position: "absolute",
              left: theme.spacing(2),
              top: theme.spacing(2),
            }}
            size="large"
          >
            <Close />
          </IconButton>

          <Typography variant="h6" component="div">
            Generate QR Code
          </Typography>

          <IconButton
            onClick={handlePrint}
            sx={{
              position: "absolute",
              right: theme.spacing(2),
              top: theme.spacing(2),
            }}
            size="large"
            title="Print QR Code"
          >
            <Print />
          </IconButton>
        </Box>

        {/* Main content - printable area */}
        <Box className="qr-code-printable">
          <Stack spacing={3} alignItems="center">
            {/* Encamp branding */}
            <Stack spacing={1} alignItems="center">
              <img
                src={EncampLogo}
                alt="Encamp logo"
                style={{ height: "60px" }}
              />
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.primary.main,
                }}
              >
                Encamp
              </Typography>
            </Stack>

            {/* QR Code */}
            <Box
              className="qr-code-image-container"
              sx={{
                padding: 2,
                backgroundColor: theme.palette.background.paper,
                border: `2px solid ${theme.palette.divider}`,
                borderRadius: 2,
              }}
            >
              {qrCodeUrl ? (
                <img src={qrCodeUrl} alt="QR Code" className="qr-code-image" />
              ) : (
                <Box
                  sx={{
                    width: 400,
                    height: 400,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <QrCode2
                    sx={{ fontSize: 100, color: theme.palette.action.disabled }}
                  />
                </Box>
              )}
            </Box>

            {/* Editable description */}
            <Box sx={{ width: "100%", maxWidth: 500 }}>
              {/* Input for screen display */}
              <input
                type="text"
                value={description}
                onChange={handleDescriptionChange}
                className="qr-code-description no-print"
                style={{
                  width: "100%",
                  textAlign: "center",
                  fontSize: "1.1rem",
                  padding: "12px",
                  borderRadius: "4px",
                  border: `1px solid ${theme.palette.divider}`,
                  backgroundColor: theme.palette.background.paper,
                  color: theme.palette.text.primary,
                  fontFamily: theme.typography.fontFamily,
                  outline: "none",
                }}
                onFocus={(e) => {
                  e.target.style.border = `2px solid ${theme.palette.primary.main}`;
                }}
                onBlur={(e) => {
                  e.target.style.border = `1px solid ${theme.palette.divider}`;
                }}
              />

              {/* Text display for print only */}
              <Typography
                className="qr-code-description-print print-only"
                sx={{
                  textAlign: "center",
                  fontSize: "1.1rem",
                  padding: "12px",
                  wordWrap: "break-word",
                  lineHeight: 1.5,
                }}
              >
                {description}
              </Typography>
            </Box>

            {/* URL display */}
            <Typography
              variant="caption"
              sx={{
                maxWidth: 500,
                wordBreak: "break-all",
                textAlign: "center",
                color: theme.palette.text.secondary,
                fontSize: "0.75rem",
              }}
            >
              {targetUrl || window.location.href}
            </Typography>
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
